import {useCallback, useEffect, useRef} from 'react';

import {BrandHandle, DeliveryEstimates} from 'utilities/delivery-promise/types';
import {Scenario} from 'utilities/promise-presenter/types';
import {ReportType} from 'utilities/error-logger';

import {
  trackDeliveryPromiseError,
  trackDeliveryPromiseRenderEvent,
} from './events';

const calculateRenderingType = (
  presentment: Scenario,
  forceDisplayDates: boolean,
  showError: boolean,
  showMarkup: boolean,
) => {
  if (showError) {
    return 'options_available_in_checkout';
  }
  if (!showMarkup) {
    return 'hidden';
  }
  if (forceDisplayDates) {
    return 'testing_mode';
  }
  switch (presentment?.brandHandle) {
    case BrandHandle.Prime:
      return 'branded_buy_with_prime';
    case BrandHandle.ShopPromise:
      return 'branded_shop_promise';
    default:
      return 'unbranded';
  }
};

const calculateNoPromiseRenderedReason = (
  showError: boolean,
  showMarkup: boolean,
  hasError: boolean,
  presentment: Scenario,
) => {
  if (showError && hasError) {
    return 'error';
  }
  if (presentment.hidePromise) {
    return `hide_promise_${presentment.noPromiseReason}`;
  }
  if (!showMarkup) {
    return 'show_markup_is_false';
  }
};

export function useTrackingRenderProcessing(options: {
  isModuleLoading: boolean;
  hasError: boolean;
  showError: boolean;
  requestId: string;
  showMarkup: boolean;
  presentment: Scenario;
  forceDisplayDates: boolean;
  deliveryEstimate: DeliveryEstimates | null;
  postalCode: string | null;
}) {
  const {
    isModuleLoading,
    hasError,
    showError,
    requestId,
    showMarkup,
    presentment,
    forceDisplayDates,
    deliveryEstimate,
    postalCode,
  } = options;
  useEffect(() => {
    if (!isModuleLoading && (deliveryEstimate || showError)) {
      trackDeliveryPromiseRenderEvent(
        requestId,
        calculateNoPromiseRenderedReason(
          showError || false,
          showMarkup,
          hasError,
          presentment,
        ),
        calculateRenderingType(
          presentment,
          forceDisplayDates,
          showError || false,
          showMarkup,
        ),
      );
    }
  }, [
    isModuleLoading,
    deliveryEstimate,
    requestId,
    showError,
    showMarkup,
    hasError,
    presentment,
    forceDisplayDates,
    postalCode,
  ]);
}

export function useTrackErrorOnlyOnce() {
  const seenErrorTypesRef = useRef<Set<string>>(new Set());

  return useCallback(
    (
      requestId: string,
      error: Error,
      message: string,
      severity: ReportType,
    ) => {
      const errorType = error.constructor.name;

      if (!seenErrorTypesRef.current.has(errorType)) {
        seenErrorTypesRef.current.add(errorType);
        trackDeliveryPromiseError(requestId, error, message, severity);
      }
    },
    [],
  );
}
